import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface TemplateRow { readonly id: string; readonly name: string; readonly channel: 'email' | 'sms' | 'push'; }

interface VariableDraft { name: string; source: string; }

export function NotificationsTemplatesPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [templateCode, setTemplateCode] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('TRANSACTIONAL');
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [language, setLanguage] = useState('en');
  const [variables, setVariables] = useState<VariableDraft[]>([{ name: '', source: '' }]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ items: TemplateRow[] }>('/notifications/templates', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      const vars = variables
        .filter((v) => v.name.trim() !== '')
        .map((v) => ({ variableName: v.name.trim(), variableSource: v.source.trim() || 'context' }));
      await apiRequest('/notifications/templates', {
        method: 'POST',
        token,
        body: {
          templateCode,
          templateName,
          templateType,
          channel,
          subject: subject || undefined,
          bodyTemplate,
          language,
          variables: vars.length > 0 ? vars : undefined,
        },
      });
      setCreated(true);
      setTemplateCode('');
      setTemplateName('');
      setSubject('');
      setBodyTemplate('');
      setVariables([{ name: '', source: '' }]);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="templates">
      <h1>{t('notif.templates.title')}</h1>

      <form onSubmit={onCreate} aria-label="template-create-form">
        <h2>{t('notif.templates.newTemplate')}</h2>
        <label htmlFor="tc-code">{t('notif.templates.templateCode')}</label>
        <input id="tc-code" value={templateCode} onChange={(e) => setTemplateCode(e.target.value)} required />
        <label htmlFor="tc-name">{t('notif.templates.templateName')}</label>
        <input id="tc-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} required />
        <label htmlFor="tc-type">{t('notif.templates.type')}</label>
        <select id="tc-type" aria-label="template-type" value={templateType} onChange={(e) => setTemplateType(e.target.value)}>
          <option value="TRANSACTIONAL">{t('notif.templates.typeTransactional')}</option>
          <option value="PROMOTIONAL">{t('notif.templates.typePromotional')}</option>
          <option value="SYSTEM">{t('notif.templates.typeSystem')}</option>
        </select>
        <label htmlFor="tc-channel">{t('notif.common.channel')}</label>
        <select id="tc-channel" aria-label="template-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="email">{t('notif.common.chEmail')}</option>
          <option value="sms">{t('notif.common.chSms')}</option>
          <option value="push">{t('notif.common.chPush')}</option>
        </select>
        <label htmlFor="tc-subject">{t('notif.common.subject')}</label>
        <input id="tc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label htmlFor="tc-body">{t('notif.common.body')}</label>
        <textarea id="tc-body" value={bodyTemplate} onChange={(e) => setBodyTemplate(e.target.value)} required />
        <label htmlFor="tc-language">{t('notif.templates.language')}</label>
        <input id="tc-language" value={language} onChange={(e) => setLanguage(e.target.value)} />

        <fieldset aria-label="template-variables">
          <legend>{t('notif.templates.variables')}</legend>
          {variables.map((v, i) => (
            <div key={i}>
              <input
                aria-label={`variable-name-${i}`}
                placeholder={t('notif.templates.varNamePh')}
                value={v.name}
                onChange={(e) => setVariables(variables.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              />
              <input
                aria-label={`variable-source-${i}`}
                placeholder={t('notif.templates.varSourcePh')}
                value={v.source}
                onChange={(e) => setVariables(variables.map((x, j) => (j === i ? { ...x, source: e.target.value } : x)))}
              />
            </div>
          ))}
          <Button type="button" onClick={() => setVariables([...variables, { name: '', source: '' }])} variant="primary">{t('notif.templates.addVariable')}</Button>
        </fieldset>

        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('notif.templates.created')}</p> : null}
        <Button type="submit" disabled={creating} variant="primary">{creating ? t('notif.common.creating') : t('notif.templates.createTemplate')}</Button>
      </form>

      <ul aria-label="templates-list">
        {rows.map((r) => (
          <li key={r.id}><Link to={`/admin/notifications/templates/${r.id}`}>{r.name}</Link> ({r.channel})</li>
        ))}
      </ul>
    </main>
  );
}
